/**
 * UsuariosView.jsx - Vista principal de lista de usuarios
 * 
 * RESPONSABILIDAD:
 * - Mostrar todos los usuarios del sistema en formato de tarjetas
 * - Permitir búsqueda de usuarios por email/nombre/teléfono
 * - Mostrar métricas rápidas de cada usuario (cantidad de tags, plan)
 * - Proveer acciones rápidas (ver tags del usuario)
 * 
 * CHANGELOG v2.0 (Oct 2025):
 * - Aplicado Design System Slate/Charcoal
 * - Eliminados badges innecesarios (planes)
 * - Mejorada jerarquía visual con variables CSS
 * - Espaciado consistente con var(--space-X)
 * - Hover states mejorados
 */

import React, { useState } from 'react';

const UsuariosView = ({ 
  usuarios, 
  loading, 
  onUserSelect, 
  onLoadMore, 
  onSearch
}) => {
  const [localSearch, setLocalSearch] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    onSearch(localSearch);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No registrada';
    
    // Si es un Timestamp de Firestore
    if (timestamp?.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString('es-MX');
    }
    // Si es una fecha string o Date
    return new Date(timestamp).toLocaleDateString('es-MX');
  };

  return (
    <div className="usuarios-view">
      {/* Barra de búsqueda */}
      <form onSubmit={handleSearch} style={{ marginBottom: 'var(--space-5)' }}>
        <div className="input-group">
          <span 
            className="input-group-text"
            style={{ 
              backgroundColor: 'var(--gray-50)',
              borderColor: 'var(--gray-300)'
            }}
          >
            <i className="bi bi-search" style={{ color: 'var(--gray-600)' }}></i>
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Buscar por email, nombre o teléfono..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            style={{
              borderColor: 'var(--gray-300)',
              fontSize: 'var(--font-size-base)'
            }}
          />
          <button 
            className="btn"
            type="submit"
            style={{
              backgroundColor: 'var(--feature-primary)',
              color: 'var(--white)',
              fontWeight: 'var(--font-weight-semibold)',
              transition: 'var(--transition-base)',
              borderColor: 'var(--feature-primary)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--feature-dark)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--feature-primary)';
            }}
          >
            Buscar
          </button>
        </div>
      </form>

      {/* Lista de usuarios */}
      {loading ? (
        <div className="text-center" style={{ padding: 'var(--space-12) 0' }}>
          <div 
            className="spinner-border" 
            role="status"
            style={{ color: 'var(--feature-primary)' }}
          >
            <span className="visually-hidden">Cargando usuarios...</span>
          </div>
        </div>
      ) : usuarios.length === 0 ? (
        <div 
          className="text-center" 
          style={{ 
            padding: 'var(--space-12) 0',
            color: 'var(--gray-500)'
          }}
        >
          <i 
            className="bi bi-people" 
            style={{ fontSize: '3rem', color: 'var(--gray-400)' }}
          ></i>
          <p style={{ 
            marginTop: 'var(--space-3)',
            fontSize: 'var(--font-size-lg)'
          }}>
            No se encontraron usuarios
          </p>
        </div>
      ) : (
        <div className="row">
          {usuarios.map(user => (
            <div key={user.id} className="col-12 col-md-6 col-lg-4 mb-3">
              <div 
                className="card h-100"
                style={{
                  border: '1px solid var(--gray-200)',
                  borderRadius: 'var(--radius-lg)',
                  transition: 'var(--transition-base)',
                  boxShadow: 'var(--shadow-sm)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--feature-accent)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--gray-200)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div 
                  className="card-body"
                  style={{ padding: 'var(--space-4)' }}
                >
                  {/* Header - Nombre y email */}
                  <div style={{ marginBottom: 'var(--space-4)' }}>
                    <h6 
                      className="card-title"
                      style={{
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--gray-900)',
                        marginBottom: 'var(--space-1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)'
                      }}
                    >
                      <i 
                        className="bi bi-person-circle"
                        style={{ color: 'var(--feature-primary)' }}
                      ></i>
                      {user.nombrePropietario || 'Usuario sin nombre'}
                    </h6>
                    
                    <small 
                      className="d-block"
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--gray-600)',
                        marginBottom: 'var(--space-1)'
                      }}
                    >
                      {user.correoPropietario}
                    </small>
                    
                    {user.telefono && (
                      <small 
                        className="d-block"
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--gray-600)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-1)'
                        }}
                      >
                        <i className="bi bi-telephone"></i>
                        {user.telefono}
                      </small>
                    )}
                  </div>
                  
                  {/* Badges informativos - SOLO los críticos */}
                  <div style={{ marginBottom: 'var(--space-4)' }}>
                    <div 
                      className="d-flex gap-2 flex-wrap"
                      style={{ gap: 'var(--space-2)' }}
                    >
                      {/* Cantidad de tags - SIEMPRE visible */}
                      <span 
                        className="badge"
                        style={{
                          backgroundColor: 'var(--feature-primary)',
                          color: 'var(--white)',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 'var(--font-weight-semibold)',
                          padding: 'var(--space-1) var(--space-2)',
                          borderRadius: 'var(--radius-full)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-1)'
                        }}
                      >
                        <i className="bi bi-tags"></i>
                        {user.cantidadObjetos || 0} tags
                      </span>
                      
                      {/* Tags activos - Solo si tiene */}
                      {user.cantidadActivos > 0 && (
                        <span 
                          className="badge"
                          style={{
                            backgroundColor: 'var(--color-success)',
                            color: 'var(--white)',
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: 'var(--font-weight-semibold)',
                            padding: 'var(--space-1) var(--space-2)',
                            borderRadius: 'var(--radius-full)'
                          }}
                        >
                          {user.cantidadActivos} activos
                        </span>
                      )}
                      
                      {/* Email verificado - Solo ícono pequeño */}
                      {user.correoVerificado && (
                        <span 
                          style={{
                            color: 'var(--color-success)',
                            fontSize: 'var(--font-size-base)',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Email verificado"
                        >
                          <i className="bi bi-check-circle-fill"></i>
                        </span>
                      )}
                    </div>
                    
                    {/* Fecha de activación */}
                    <div style={{ marginTop: 'var(--space-2)' }}>
                      <small 
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--gray-500)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-1)'
                        }}
                      >
                        <i className="bi bi-calendar-check"></i>
                        {formatDate(user.fechaActivacion)}
                      </small>
                    </div>
                  </div>
                  
                  {/* Botón de acción */}
                  <button 
                    className="btn w-100"
                    onClick={() => onUserSelect(user.id)}
                    style={{
                      backgroundColor: 'transparent',
                      color: 'var(--feature-primary)',
                      border: '1px solid var(--feature-primary)',
                      fontWeight: 'var(--font-weight-semibold)',
                      fontSize: 'var(--font-size-sm)',
                      padding: 'var(--space-2) var(--space-3)',
                      borderRadius: 'var(--radius-md)',
                      transition: 'var(--transition-base)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 'var(--space-2)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--feature-primary)';
                      e.currentTarget.style.color = 'var(--white)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'var(--feature-primary)';
                    }}
                  >
                    <i className="bi bi-eye"></i>
                    Ver Tags
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Botón cargar más */}
      {usuarios.length > 0 && !loading && (
        <div 
          className="text-center"
          style={{ marginTop: 'var(--space-5)' }}
        >
          <button 
            className="btn"
            onClick={onLoadMore}
            style={{
              backgroundColor: 'transparent',
              color: 'var(--feature-primary)',
              border: '1px solid var(--feature-primary)',
              fontWeight: 'var(--font-weight-semibold)',
              fontSize: 'var(--font-size-base)',
              padding: 'var(--space-3) var(--space-6)',
              borderRadius: 'var(--radius-md)',
              transition: 'var(--transition-base)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--feature-primary)';
              e.currentTarget.style.color = 'var(--white)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--feature-primary)';
            }}
          >
            Cargar más usuarios
          </button>
        </div>
      )}
    </div>
  );
};

export default UsuariosView;